import { Role, RolePerformerType, RoleType } from '@talon/web/lib/api';
import { BoxProps } from 'grommet';
import { User } from 'grommet-icons';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef } from 'react';
import { filterRoleTypes } from 'src/utils/listFilters';
import { Panel } from '..';
import { useProjectStore, useRoleStore } from '../../../context';

export interface IRolePanelProps {
  scriptId?: string;
  roles: Role[];
  setRoles: (roles: Role[]) => void;
  cardProps?: BoxProps;
  headerBackground?: string;
}

export const RolePanel = observer(({ scriptId, roles, setRoles, cardProps, headerBackground }: IRolePanelProps) => {
  /** Context **/
  const roleStore = useRoleStore();
  const projectStore = useProjectStore();

  /** Refs **/
  const rolesAdded = useRef(0);

  /** Methods **/

  const newRole = async () => {
    if (!projectStore.currentProject?.id || !scriptId) return;
    const roleRequest = {
      name: `New Role ${(rolesAdded.current += 1)}`,
      type: RoleType.Actor,
      performerType: RolePerformerType.OnCameraPrincipal,
      scriptId: scriptId,
      projectId: projectStore.currentProject.id,
    };
    const role = await roleStore.createRole(roleRequest);
    roles && role && setRoles([...roles, role]);
  };

  const deleteRoles = async (ids: string[]) => {
    ids.forEach(async (id) => await roleStore.deleteRole(id));
    setRoles(roles.filter((r) => !ids.includes(r.id)));
  };

  const updateRole = async (update: Role, index: number) => {
    const role = await roleStore.updateRole(update);
    if (!role) return;
    const newRoles = [...roles];
    newRoles[index] = role;
    setRoles(newRoles);
  };

  /** Effects **/

  useEffect(() => {
    if (!scriptId) return;
    const getRoles = async () => {
      const roles = await roleStore.getRolesByScriptId(scriptId);
      if (roles) {
        setRoles(roles);
        if (!rolesAdded.current) rolesAdded.current = roles.length;
      }
    };
    getRoles();
  }, [scriptId]);

  /** Render **/
  return (
    <Panel
      items={roles}
      newItem={newRole}
      deleteItems={deleteRoles}
      updateItem={updateRole}
      label="Role"
      labelPlural="Roles"
      addIcon={<User />}
      types={[
        {
          label: 'Performer Type',
          key: 'performer_type',
          values: Object.values(RolePerformerType).filter((v) => !v.includes('Group')),
        },
        {
          label: 'Role Type',
          key: 'type',
          values: Object.values(RoleType),
          filterKey: 'performer_type',
          filterFunction: filterRoleTypes,
        },
      ]}
      cardProps={cardProps}
      headerBackground={headerBackground}
    />
  );
});
